import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react';

import { composeVariants, cva, cx } from '../../utils/cva';
import { dataFocusRing } from '../FocusVisible';

export interface RadioGroupProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    'dir' | 'orientation'
  > {
  'data-testid'?: string;
}

export const RadioGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>((props, ref) => {
  const {
    className,
    'data-testid': dataTestId = 'radio-group',
    ...rest
  } = props;

  return (
    <RadioGroupPrimitive.Root
      data-testid={dataTestId}
      className={cx('flex', 'gap-1', 'flex-col', className)}
      {...rest}
      ref={ref}
    />
  );
});

const INDICATOR_SVG = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="currentColor"
  >
    <title>Radio Indicator</title>
    <circle
      className={cx('invisible', 'group-data-[state=checked]/radio:visible')}
      cx="8"
      cy="8"
      r="4"
    />
  </svg>
);

const radioGroupItemVariants = composeVariants(
  dataFocusRing,
  cva({
    base: [
      'group/radio',
      'inline-block',
      'm-1',
      'h-4',
      'w-4',
      'rounded-full',
      'shrink-0',
      'ring-1.5',
      'ring-inset',
      'ring-content-default',
      'data-[state=checked]:ring-surface-state-selected',
      'data-[state=checked]:hover:ring-surface-state-selected-hover',
      'bg-default',
      'text-surface-state-selected',
      'hover:text-surface-state-selected-hover',
      'hover:bg-default-hover',
      'disabled:!ring-content-state-disabled',
      'disabled:!text-state-disabled',
      'data-[disabled]:!ring-content-state-disabled',
      'data-[disabled]:!text-state-disabled',
    ],
  }),
);

const radioGroupIndicatorVariants = cva({
  base: ['flex', 'items-center', 'justify-center'],
});

export interface RadioGroupItemProps
  extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  indicatorProps?: ComponentProps<typeof RadioGroupPrimitive.Indicator>;
  dataTestId?: string;
}

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>((props, ref) => {
  const {
    className,
    children,
    indicatorProps = {},
    dataTestId = 'radio-group-control',
    ...rest
  } = props;

  const { className: indicatorClassName, ...restIndicatorProps } =
    indicatorProps;

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      data-testid={dataTestId}
      className={radioGroupItemVariants({ className })}
      {...rest}
    >
      <RadioGroupPrimitive.Indicator
        className={radioGroupIndicatorVariants({
          className: indicatorClassName,
        })}
        data-testid={`${dataTestId}-indicator`}
        {...restIndicatorProps}
      >
        {INDICATOR_SVG}
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

const radioGroupDummyItemVariants = composeVariants(
  radioGroupItemVariants,
  cva({
    base: [
      'group-data-[state=checked]/radio:ring-surface-state-selected',
      'group-data-[state=checked]/radio:group-hover/radio:ring-surface-state-selected-hover',
      'group-hover/radio:text-surface-state-selected-hover',
      'group-hover/radio:bg-default-hover',
      'group-disabled/radio:!ring-content-state-disabled',
      'group-disabled/radio:!text-state-disabled',
      'group-data-[disabled]/radio:!ring-content-state-disabled',
      'group-data-[disabled]/radio:!text-state-disabled',
    ],
  }),
);

export interface RadioGroupDummyItemProps
  extends ComponentPropsWithoutRef<'span'> {
  indicatorProps?: ComponentProps<typeof RadioGroupPrimitive.Indicator>;
}

export const RadioGroupDummyItem = forwardRef<
  ElementRef<'span'>,
  RadioGroupDummyItemProps
>((props, ref) => {
  const { className, indicatorProps = {}, ...rest } = props;

  const { className: indicatorClassName, ...restIndicatorProps } =
    indicatorProps;

  return (
    <span
      ref={ref}
      className={radioGroupDummyItemVariants({ className })}
      {...rest}
    >
      <span
        className={radioGroupIndicatorVariants({
          className: indicatorClassName,
        })}
        {...restIndicatorProps}
      >
        {INDICATOR_SVG}
      </span>
    </span>
  );
});
